import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Leaf, Ship, DollarSign, TrendingDown, AlertTriangle,
  Loader2, RefreshCw, Gauge, BarChart3
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, LineChart, Line
} from 'recharts';

const CII_COLORS = { A: '#16A34A', B: '#22C55E', C: '#F59E0B', D: '#F97316', E: '#E11D48', 'N/A': '#94A3B8' };

function StatCard({ title, value, icon: Icon, color, sub }) {
  return (
    <Card className="bg-white border border-slate-200 rounded-sm shadow-none hover:border-slate-300 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{title}</p>
            <p className="font-mono text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
          </div>
          <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${color}`}>
            <Icon className="h-4 w-4 text-white stroke-[1.5px]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function EmissionsDashboardPage() {
  const [portfolio, setPortfolio] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [voyages, setVoyages] = useState([]);
  const [carbonCosts, setCarbonCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [p, f, v, c] = await Promise.all([
        api.get('/emissions/portfolio'),
        api.get('/emissions/cii-fleet'),
        api.get('/emissions/voyages'),
        api.get('/emissions/carbon-costs'),
      ]);
      setPortfolio(p.data);
      setFleet(f.data);
      setVoyages(v.data);
      setCarbonCosts(c.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCalculateFleet = async () => {
    setCalculating(true);
    try {
      await api.post('/emissions/calculate-fleet');
      // Now calculate carbon costs for each emissions record
      const { data: newVoyages } = await api.get('/emissions/voyages');
      for (const v of newVoyages) {
        if (v.id) {
          try {
            await api.post('/emissions/carbon-cost', { emissions_id: v.id });
          } catch {}
        }
      }
      await loadData();
    } catch (e) { console.error(e); }
    finally { setCalculating(false); }
  };

  const routeChartData = (portfolio?.emissions_by_route || []).slice(0, 8).map(r => ({
    route: r.route.length > 25 ? r.route.substring(0, 22) + '...' : r.route,
    co2e: r.total_co2e,
  }));

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="emissions-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="emissions-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Emissions Intelligence</h1>
          <p className="text-sm text-slate-500 mt-1">Carbon emissions tracking, CII compliance & EU ETS cost attribution</p>
        </div>
        <Button
          onClick={handleCalculateFleet}
          disabled={calculating}
          data-testid="calculate-fleet-emissions-button"
          className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white text-xs"
        >
          {calculating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Calculate Fleet Emissions
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="emissions-kpis">
        <StatCard
          title="Total Portfolio CO2e"
          value={`${(portfolio?.total_co2e_tons || 0).toLocaleString()} t`}
          icon={Leaf}
          color="bg-[#16A34A]"
          sub={`${portfolio?.voyage_count || 0} voyages calculated`}
        />
        <StatCard
          title="Carbon Cost (EU ETS)"
          value={`$${(portfolio?.total_carbon_cost || 0).toLocaleString()}`}
          icon={DollarSign}
          color="bg-[#0A2463]"
          sub={portfolio?.avg_carbon_cost_per_mmbtu ? `$${portfolio.avg_carbon_cost_per_mmbtu.toFixed(4)}/MMBtu` : 'No data'}
        />
        <StatCard
          title="Avg CO2e/MMBtu"
          value={portfolio?.avg_co2e_per_mmbtu ? portfolio.avg_co2e_per_mmbtu.toFixed(4) : '—'}
          icon={TrendingDown}
          color="bg-[#F59E0B]"
          sub="Tonnes per MMBtu"
        />
        <StatCard
          title="Avg EEOI"
          value={portfolio?.avg_eeoi ? portfolio.avg_eeoi.toFixed(2) : '—'}
          icon={Gauge}
          color="bg-[#007AFF]"
          sub="gCO2/ton-nm"
        />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="rounded-sm h-9 bg-slate-100 p-0.5">
          <TabsTrigger value="overview" className="rounded-sm text-xs flex-1" data-testid="tab-emissions-overview">
            <BarChart3 className="h-3 w-3 mr-1" />Overview
          </TabsTrigger>
          <TabsTrigger value="cii" className="rounded-sm text-xs flex-1" data-testid="tab-cii-fleet">
            <Ship className="h-3 w-3 mr-1" />CII Fleet Tracker
          </TabsTrigger>
          <TabsTrigger value="voyages" className="rounded-sm text-xs flex-1" data-testid="tab-voyage-emissions">
            <Leaf className="h-3 w-3 mr-1" />Voyage Emissions
          </TabsTrigger>
          <TabsTrigger value="carbon" className="rounded-sm text-xs flex-1" data-testid="tab-carbon-costs">
            <DollarSign className="h-3 w-3 mr-1" />Carbon Costs
          </TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <div className="space-y-4">
            {/* Emissions by Route */}
            {routeChartData.length > 0 && (
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-800">Emissions by Route (CO2e tons)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={routeChartData} margin={{ left: 10, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="route" tick={{ fontSize: 9, fill: '#64748b' }} angle={-20} textAnchor="end" height={60} />
                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                        <RechartsTooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} />
                        <Bar dataKey="co2e" fill="#16A34A" radius={[2, 2, 0, 0]} barSize={24} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Monthly Trend */}
            {(portfolio?.monthly_trend || []).length > 0 && (
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-800">Monthly Emissions Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={portfolio.monthly_trend.slice().reverse()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={v => v ? new Date(v).toLocaleDateString('en', { month: 'short', year: '2-digit' }) : ''} />
                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                        <RechartsTooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} />
                        <Line type="monotone" dataKey="co2e_tons" stroke="#16A34A" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {routeChartData.length === 0 && (portfolio?.monthly_trend || []).length === 0 && (
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardContent className="py-24 text-center">
                  <Leaf className="h-12 w-12 mx-auto text-slate-200 stroke-[1px]" />
                  <p className="mt-4 text-sm text-slate-400">No emissions data yet</p>
                  <p className="text-xs text-slate-300 mt-1">Click "Calculate Fleet Emissions" to generate data for all tracked vessels</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* CII Fleet Tracker */}
        <TabsContent value="cii">
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-800">Fleet CII Rating Matrix</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="cii-fleet-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Vessel</th>
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">IMO</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Voyages</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Total CO2e</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Avg CII</th>
                    <th className="text-center text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {fleet.map(v => (
                    <tr key={v.vessel_id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`cii-row-${v.imo}`}>
                      <td className="px-5 py-2.5 text-xs font-medium text-slate-800">{v.name}</td>
                      <td className="px-5 py-2.5 text-xs font-mono text-slate-500">{v.imo}</td>
                      <td className="px-5 py-2.5 text-xs font-mono text-right text-slate-600">{v.voyage_count}</td>
                      <td className="px-5 py-2.5 text-xs font-mono text-right text-slate-600">
                        {v.total_emissions_tons > 0 ? `${v.total_emissions_tons.toLocaleString()} t` : '—'}
                      </td>
                      <td className="px-5 py-2.5 text-xs font-mono text-right text-slate-600">
                        {v.avg_cii > 0 ? v.avg_cii.toFixed(4) : '—'}
                      </td>
                      <td className="px-5 py-2.5 text-center">
                        <span
                          className="inline-flex items-center justify-center w-7 h-7 rounded-sm text-xs font-bold text-white"
                          style={{ backgroundColor: CII_COLORS[v.current_rating] || CII_COLORS['N/A'] }}
                        >
                          {v.current_rating}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Voyage Emissions */}
        <TabsContent value="voyages">
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardContent className="p-0">
              {voyages.length === 0 ? (
                <div className="py-16 text-center text-sm text-slate-400">No voyage emissions calculated yet</div>
              ) : (
                <table className="w-full text-sm" data-testid="voyage-emissions-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Vessel</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Distance</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">CO2 Comb.</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">CO2 Boiloff</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">CH4 Slip</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Total CO2e</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">EEOI</th>
                      <th className="text-center text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">CII</th>
                    </tr>
                  </thead>
                  <tbody>
                    {voyages.map(v => (
                      <tr key={v.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-5 py-2 text-xs font-medium text-slate-800">{v.vessel_name || '—'}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{v.voyage_distance_nm ? `${parseFloat(v.voyage_distance_nm).toLocaleString()} NM` : '—'}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{parseFloat(v.co2_combustion_tons || 0).toFixed(1)} t</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{parseFloat(v.co2_boiloff_tons || 0).toFixed(1)} t</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{parseFloat(v.methane_slip_co2e_tons || 0).toFixed(1)} t</td>
                        <td className="px-5 py-2 text-xs font-mono text-right font-bold text-slate-900">{parseFloat(v.total_co2e_tons || 0).toFixed(1)} t</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{parseFloat(v.eeoi || 0).toFixed(2)}</td>
                        <td className="px-5 py-2 text-center">
                          <span
                            className="inline-flex items-center justify-center w-6 h-6 rounded-sm text-[10px] font-bold text-white"
                            style={{ backgroundColor: CII_COLORS[v.cii_rating] || CII_COLORS['N/A'] }}
                          >
                            {v.cii_rating || '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Carbon Costs */}
        <TabsContent value="carbon">
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardContent className="p-0">
              {carbonCosts.length === 0 ? (
                <div className="py-16 text-center text-sm text-slate-400">No carbon costs calculated yet</div>
              ) : (
                <table className="w-full text-sm" data-testid="carbon-costs-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Vessel</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Regulation</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">CO2e (t)</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Price/t</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Total Cost</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">$/MT</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">$/MMBtu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {carbonCosts.map(c => (
                      <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-5 py-2 text-xs font-medium text-slate-800">{c.vessel_name || '—'}</td>
                        <td className="px-5 py-2">
                          <Badge variant="outline" className="rounded-sm text-[9px] uppercase">{c.regulation}</Badge>
                        </td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{parseFloat(c.total_co2e_tons || 0).toFixed(1)}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">${parseFloat(c.carbon_price_per_ton || 0).toFixed(0)}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right font-bold text-slate-900">${parseFloat(c.total_carbon_cost || 0).toLocaleString()}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">${parseFloat(c.carbon_cost_per_mt || 0).toFixed(2)}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">${parseFloat(c.carbon_cost_per_mmbtu || 0).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
